import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Cpu,
  ExternalLink,
  HardDrive,
  HelpCircle,
  LifeBuoy,
  MemoryStick,
  Network,
  Server,
  XCircle,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";
import {
  BusyOverlay,
  PROVIDER_WAIT_HINT,
} from "@/components/common/BusyOverlay";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useExecuteVmRecovery,
  useProbeVmRecovery,
} from "@/hooks/useResourceQueries";
import { cn, formatDateTime } from "@/lib/utils";
import type {
  SpecMismatchField,
  VmRecoveryExecution,
  VmRecoveryProbe,
} from "@/types/admin";

interface RecoverVmDialogProps {
  /** The VM to recover. */
  resourceId: string;
  /** Name shown in the heading, so the dialog identifies the VM before anything loads. */
  resourceName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Whether the recovery covers the whole page while it runs. The resource table passes `false`
   * and covers its own Card instead, driven by `onPendingChange`.
   */
  pageOverlay?: boolean;
  /** Mirrors whether the recovery is running, for a caller that shows its own busy state. */
  onPendingChange?: (pending: boolean) => void;
}

/**
 * The screens this dialog moves through.
 *
 * Opening the dialog IS the operator asking, so the probe fires on mount and the first thing they see
 * is what the infrastructure actually says. The review step always carries a UUID field, pre-filled
 * with the recorded one: the operator can check a different machine and re-link the VM to it.
 */
type Phase = "checking" | "review" | "running" | "done";

const FIELD_ICONS: Record<string, typeof Cpu> = {
  cores: Cpu,
  memory: MemoryStick,
  disk: HardDrive,
};

/** A figure with its unit, or an em dash when the side holds nothing. */
function formatValue(value: number | null, unit: string | null): string {
  if (value === null || value === undefined) return "—";
  return unit ? `${value} ${unit}` : `${value}`;
}

function fieldStatusBadge(field: SpecMismatchField) {
  switch (field.status) {
    case "Mismatch":
      return <Badge variant="warning">Differs</Badge>;
    case "MissingLocal":
      return <Badge variant="warning">Not recorded</Badge>;
    case "MissingProvider":
      return <Badge variant="secondary">Not reported</Badge>;
    default:
      return <Badge variant="outline">Match</Badge>;
  }
}

/** Whether an outcome means the run finished everything it set out to do. */
function isComplete(outcome: VmRecoveryExecution["outcome"]): boolean {
  return outcome === "Recovered" || outcome === "AlreadyRecovered";
}

/**
 * Brings a VM back into service after its provisioning died half-way and the infrastructure team
 * fixed the machine by hand.
 *
 * WiCloud never learns that on its own: the status sync only revisits resources already Running or
 * Stopped, so an Error row stays broken for ever while the customer is billed for it. This reads the
 * machine, writes it onto the record, brings the resource back and closes the order.
 *
 * Three things it deliberately does not do: it sends the customer no email, it changes no dates or
 * prices, and it never moves a payment into Completed — a payment is read only to earn the right to
 * close the order.
 *
 * Nothing here changes or deletes anything on the infrastructure. It adopts the machine the resource
 * is recorded against, or one the operator types instead (decided 2026-09-15) once the infrastructure
 * confirms that machine, nothing locks it and no other resource holds it. Re-linking leaves the
 * previous machine running on the infrastructure, no longer tracked by WiCloud.
 */
export function RecoverVmDialog({
  resourceId,
  resourceName,
  open,
  onOpenChange,
  pageOverlay = true,
  onPendingChange,
}: RecoverVmDialogProps) {
  const [phase, setPhase] = useState<Phase>("checking");
  const [probe, setProbe] = useState<VmRecoveryProbe | null>(null);
  const [execution, setExecution] = useState<VmRecoveryExecution | null>(null);
  // Bumped by Retry to run the probe again. The probe used to fire only on open, so a failed check
  // left Close as the only way forward and the operator reopened the dialog to ask the same thing.
  const [probeAttempt, setProbeAttempt] = useState(0);
  // The UUID field, pre-filled with the recorded UUID once the first probe answers.
  const [uuidInput, setUuidInput] = useState("");

  const probeRecovery = useProbeVmRecovery();
  const executeRecovery = useExecuteVmRecovery();

  const probeRef = useRef(probeRecovery.mutateAsync);
  probeRef.current = probeRecovery.mutateAsync;

  const isRecovering = phase === "running";

  // Mirrored for a caller that covers its own area instead of the page (see `pageOverlay`), and
  // released on unmount too, so a dialog that goes away cannot leave that caller's overlay up.
  useEffect(() => {
    onPendingChange?.(isRecovering);
  }, [isRecovering, onPendingChange]);
  useEffect(() => () => onPendingChange?.(false), [onPendingChange]);

  // Asks the infrastructure as soon as the dialog opens. The call is slow and outbound, which is
  // why the dialog is mounted only while open — reaching this effect already means an operator
  // asked for it.
  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setPhase("checking");
    setProbe(null);
    setExecution(null);

    probeRef
      .current({ id: resourceId })
      .then((answer) => {
        if (cancelled) return;
        // Fills the field with the machine the VM is recorded against.
        setUuidInput(answer.recordedVmUuid ?? "");
        setProbe(answer);
        setPhase("review");
      })
      .catch(() => {
        // Rendered from probeRecovery.error below; the phase stays on `checking`, which is where
        // that message belongs.
      });

    return () => {
      cancelled = true;
    };
  }, [open, resourceId, probeAttempt]);

  const blockers = probe?.blockers ?? [];

  // The machine the last probe answered for. Falls back to the recorded UUID: an Admin API older than
  // `checkedVmUuid` probes exactly that one, and without the fallback every VM stayed disabled there.
  const checkedUuid = probe?.checkedVmUuid ?? probe?.recordedVmUuid ?? null;
  // The UUID in the field is the one being adopted, so it has to be the value that was checked —
  // typing after the check disables the run until it is checked again.
  const enteredUuidChecked =
    uuidInput.trim() !== "" &&
    uuidInput.trim().toLowerCase() === checkedUuid?.toLowerCase();
  // Sent only when it is not the recorded machine, so recovering a VM as it stands takes the same
  // request it always did.
  const relinkingTo =
    checkedUuid &&
    checkedUuid.toLowerCase() !== probe?.recordedVmUuid?.toLowerCase()
      ? checkedUuid
      : undefined;

  // Warnings deliberately do not count. They are things to know, not things to stop for. Neither does
  // the comparison: a Running or Stopped VM that already matches its machine can still be recovered —
  // the run reports "already recovered" and settles an order left behind.
  const canRun =
    Boolean(probe?.vmExists) &&
    blockers.length === 0 &&
    enteredUuidChecked &&
    !probeRecovery.isPending;

  /**
   * Checks the UUID in the field. Stays on the review step while it runs — keeping the previous answer
   * on screen — so a failed or mistyped check can be corrected in place rather than stranding the
   * dialog on the checking step, whose Retry would resend the same UUID.
   */
  const handleCheckUuid = async () => {
    const uuid = uuidInput.trim();
    if (!uuid) return;
    try {
      setProbe(
        await probeRecovery.mutateAsync({ id: resourceId, vmUuid: uuid }),
      );
    } catch {
      // Shown under the field from probeRecovery.error; the previous answer stays.
    }
  };

  const handleRecover = async () => {
    setPhase("running");
    try {
      const result = await executeRecovery.mutateAsync({
        id: resourceId,
        vmUuid: relinkingTo,
      });
      setExecution(result);
      setPhase("done");
    } catch {
      // The error surfaces from the mutation below; going back to review keeps the answer the
      // operator was looking at.
      setPhase("review");
    }
  };

  const heading = probe?.resourceName ?? resourceName;

  return (
    <Dialog
      open={open}
      // Escape and the corner X are ignored while the recovery runs. The dialog is mounted only
      // while open, so closing it mid-run unmounted the one place the outcome is reported — and this
      // flow raises no toast, so a run that stopped part-way finished with nobody seeing it.
      // ResourceConfigChangeDialog guards its own run the same way.
      onOpenChange={(next) => {
        if (next || phase !== "running") onOpenChange(next);
      }}
    >
      {/* max-h/overflow are not decoration: the review step stacks a verdict, a comparison panel, a
          context row and up to three alerts, and a blocked probe adds more. Without them a tall
          answer overflows a 1366x768 laptop at BOTH ends — the title cut off above, the confirm
          button below the fold with nothing scrollable to reach it. Same pair
          ResourceConfigChangeDialog carries, for the same reason. */}
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            Recover VM from infrastructure{heading ? ` · ${heading}` : ""}
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <DialogDescription>
            Copies the running machine onto WiCloud&rsquo;s record, returns the
            resource to service and closes its order. No customer email, and no
            change to dates, prices or payments.
          </DialogDescription>
          {phase === "checking" && (
            <CheckingStep
              error={probeRecovery.error}
              onRetry={() => setProbeAttempt((attempt) => attempt + 1)}
            />
          )}

          {phase === "review" && probe && (
            <EnterUuidField
              value={uuidInput}
              onChange={setUuidInput}
              onCheck={() => void handleCheckUuid()}
              checking={probeRecovery.isPending}
              checkError={probeRecovery.error?.message ?? null}
              recordedVmUuid={probe.recordedVmUuid}
              // Only while the field still holds the checked UUID: after typing another one the
              // warning would name a machine that is no longer the one being linked.
              relinkingTo={enteredUuidChecked ? relinkingTo : undefined}
            />
          )}

          {(phase === "review" || phase === "running") && probe && (
            <ReviewStep
              probe={probe}
              blockers={blockers}
              executeError={executeRecovery.error}
            />
          )}

          {phase === "done" && execution && <DoneStep execution={execution} />}
        </DialogBody>

        <DialogFooter className="gap-2">
          {/* Pushed to the far left, and shown in every phase: the operator most needs this while
              they are deciding, and again while reading an outcome they did not expect. A popover
              rather than a link, so reading it never navigates away from a dialog they are mid-way
              through — the long version is one click further on, in its own tab. */}
          <HelpPopover />

          {phase === "checking" && (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {probeRecovery.isPending ? "Cancel" : "Close"}
            </Button>
          )}

          {phase === "review" && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleRecover} disabled={!canRun}>
                <LifeBuoy className="mr-2 h-4 w-4" />
                Recover this VM
              </Button>
            </>
          )}

          {phase === "running" && (
            <Button disabled>
              <Spinner className="mr-2 h-4 w-4" />
              Recovering…
            </Button>
          )}

          {phase === "done" && (
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          )}
        </DialogFooter>
        {/* Covers the page as well as the dialog: the recovery writes the resource, its order and its
            configuration, and nothing behind it — the actions menu, another dialog — should start a
            second write on the same VM before this one reports back. A table that opens this passes
            `pageOverlay={false}` and covers its own Card instead. */}
        <BusyOverlay
          open={isRecovering && pageOverlay}
          label="Recovering the VM…"
          hint={PROVIDER_WAIT_HINT}
        />
      </DialogContent>
    </Dialog>
  );
}

/**
 * The short answer to "what will this actually do to the customer's VM", opened in place.
 *
 * Kept to the two things an operator has to be sure of before pressing — what it changes, and what
 * it provably leaves alone. Everything else (outcomes, refusals, the audit trail) lives in the full
 * guide, which is a page rather than more popover.
 */
function HelpPopover() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="sm:mr-auto">
          <HelpCircle className="mr-2 h-4 w-4" />
          How this works
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="top"
        className="w-80 space-y-3 text-sm"
      >
        <p className="text-muted-foreground">
          For a VM whose build never finished but which the infrastructure team
          has since fixed by hand. WiCloud never notices that on its own — the
          status sync only revisits resources that are already Running or
          Stopped.
        </p>

        <div className="space-y-1.5">
          <p className="font-medium">It reads the machine, then</p>
          <ul className="space-y-1 text-muted-foreground">
            <li>· writes its real config onto this record</li>
            <li>· brings the resource back into service</li>
            <li>· closes the order that bought it</li>
          </ul>
        </div>

        <div className="space-y-1.5">
          <p className="font-medium">It never</p>
          <ul className="space-y-1 text-muted-foreground">
            <li>· changes or deletes anything on the infrastructure</li>
            <li>
              · touches a payment, a price, the billing period or the expiry
            </li>
            <li>· emails the customer</li>
          </ul>
        </div>

        <p className="text-muted-foreground">
          Running it twice is safe — a second run reports that there was nothing
          left to do.
        </p>

        <p className="text-muted-foreground">
          It also finishes an “Attach an existing VM” whose copy failed. Do that
          right away: the customer can delete a VM on Error, and deleting this
          one deletes the real machine.
        </p>

        <Button variant="outline" size="sm" asChild className="w-full">
          <a href="/docs/vm-recovery.html" target="_blank" rel="noreferrer">
            Full guide
            <ExternalLink className="ml-2 h-3.5 w-3.5" />
          </a>
        </Button>
      </PopoverContent>
    </Popover>
  );
}

/**
 * The UUID of the machine to recover against, pre-filled with the recorded one. Checking a different
 * one re-runs the probe against that machine; the verdict below then describes it.
 */
function EnterUuidField({
  value,
  onChange,
  onCheck,
  checking,
  checkError,
  recordedVmUuid,
  relinkingTo,
}: {
  value: string;
  onChange: (next: string) => void;
  onCheck: () => void;
  checking: boolean;
  checkError: string | null;
  recordedVmUuid: string | null;
  relinkingTo?: string;
}) {
  return (
    <div className="space-y-2 rounded-lg border p-3">
      <Label htmlFor="recoveryVmUuid">VM UUID</Label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          id="recoveryVmUuid"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onCheck();
            }
          }}
          placeholder="e.g. 2cd5ed90-1b4f-4c3e-9a51-7f0e8d6b2a11"
          className="font-mono"
          autoComplete="off"
          spellCheck={false}
          aria-invalid={checkError ? true : undefined}
          aria-describedby={
            checkError
              ? "recoveryVmUuid-help recoveryVmUuid-error"
              : "recoveryVmUuid-help"
          }
        />
        <Button
          type="button"
          variant="outline"
          onClick={onCheck}
          disabled={checking || value.trim() === ""}
          className="shrink-0"
        >
          {checking && <Spinner className="mr-2 h-4 w-4" />}
          Check VM
        </Button>
      </div>
      {checkError && (
        <p id="recoveryVmUuid-error" className="text-xs text-destructive-ink">
          {checkError}
        </p>
      )}
      <p id="recoveryVmUuid-help" className="text-xs text-muted-foreground">
        {recordedVmUuid
          ? "The machine this VM is recorded against. Enter another UUID and check it to re-link the VM to that machine."
          : "This resource has no machine recorded. Enter the UUID of the machine it runs on."}{" "}
        It must exist on the infrastructure, not be locked, and not belong to
        another resource.
      </p>
      {relinkingTo && recordedVmUuid && (
        <Alert className="border-warn/25 bg-warn-soft [&>svg]:text-warn">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Re-linking to a different machine</AlertTitle>
          <AlertDescription>
            This VM will point at{" "}
            <span className="font-mono">{relinkingTo}</span> instead of{" "}
            <span className="font-mono">{recordedVmUuid}</span>. The previous
            machine keeps running on the infrastructure and is no longer tracked
            or billed by WiCloud.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}

/** Step 1 — asking the infrastructure about the machine this resource is recorded against. */
function CheckingStep({
  error,
  onRetry,
}: {
  error: Error | null;
  onRetry: () => void;
}) {
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Could not check the VM</AlertTitle>
        <AlertDescription>
          <p>{error.message}</p>
          {/* A new attempt clears the error the moment it starts, so this step falls back to its
              spinner on its own — no pending state to track here. */}
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="mt-3"
            onClick={onRetry}
          >
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex items-center gap-3 rounded-lg border p-4 text-sm text-muted-foreground">
      <Spinner className="h-4 w-4" />
      Asking the infrastructure about this VM. This can take a few seconds.
    </div>
  );
}

/** What the infrastructure says, and what recovering would do. */
function ReviewStep({
  probe,
  blockers,
  executeError,
}: {
  probe: VmRecoveryProbe;
  blockers: VmRecoveryProbe["blockers"];
  executeError: Error | null;
}) {
  const warnings = probe.warnings ?? [];

  // Only when a UUID was actually asked about. With none on record nothing was sent to the
  // infrastructure, so "it did not answer" would be untrue — the VM_UUID_REQUIRED blocker says the
  // real reason in full and is left to render on its own.
  const notFound =
    !probe.vmExists && Boolean(probe.checkedVmUuid ?? probe.recordedVmUuid);

  // The missing machine gets ONE alert, not two. Its blocker carries the sentence worth reading, so
  // it is lifted into the titled alert rather than repeated underneath it — the provider's own reply
  // is quoted below as supporting detail, which is all it is (n8n answers in Vietnamese).
  const notFoundBlocker = notFound
    ? blockers.find((blocker) => blocker.code === "VM_NOT_FOUND_ON_PROVIDER")
    : undefined;
  const otherBlockers = notFoundBlocker
    ? blockers.filter((blocker) => blocker !== notFoundBlocker)
    : blockers;

  return (
    <div className="space-y-3">
      {notFound ? (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertTitle>No such VM on the infrastructure</AlertTitle>
          <AlertDescription className="space-y-1">
            <p>
              {notFoundBlocker?.message ??
                "The infrastructure did not answer for this UUID."}
            </p>
            {probe.providerError && (
              <p className="text-xs opacity-80">
                It replied: {probe.providerError}
              </p>
            )}
          </AlertDescription>
        </Alert>
      ) : probe.vmExists && blockers.length === 0 ? (
        // Suppressed while anything blocks the run: a green "the machine is there" stacked directly
        // on top of a red "cannot recover" reads as two verdicts disagreeing. The machine's
        // configuration is still shown below, which is what the operator came to see.
        <Verdict probe={probe} />
      ) : null}

      {otherBlockers.map((blocker) => (
        <Alert key={blocker.code} variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertTitle>Cannot recover</AlertTitle>
          <AlertDescription>{blocker.message}</AlertDescription>
        </Alert>
      ))}

      {warnings.map((warning) => (
        <Alert
          key={warning.code}
          className="border-warn/25 bg-warn-soft [&>svg]:text-warn"
        >
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Worth knowing before you run this</AlertTitle>
          <AlertDescription>{warning.message}</AlertDescription>
        </Alert>
      ))}

      {probe.comparison && probe.comparison.fields.length > 0 && (
        <ConfigurationPanel
          fields={probe.comparison.fields}
          provider={probe.provider}
        />
      )}

      <ContextRow probe={probe} />

      {executeError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>The recovery did not start</AlertTitle>
          <AlertDescription>{executeError.message}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}

/**
 * One line: the machine, its power state, and what this resource becomes.
 *
 * Deliberately not an <Alert>. The UUID is the one identifier an operator cross-checks against the
 * hypervisor, so it is stated once, here, in mono — and nowhere else on the screen. It used to be
 * repeated in a second panel below, which is what made the step read as three overlapping summaries
 * of the same machine.
 */
function Verdict({ probe }: { probe: VmRecoveryProbe }) {
  const power =
    probe.provider?.qmpStatus ?? probe.provider?.status ?? "an unknown state";

  return (
    <div className="flex items-start gap-2.5 rounded-lg border border-ok/25 bg-ok-soft p-3 text-sm">
      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-ok" />
      <div className="min-w-0 space-y-0.5">
        <p className="font-medium">
          The machine is there, reported as {power}
          {probe.targetStatus
            ? ` — this resource becomes ${probe.targetStatus}`
            : ""}
        </p>
        <p className="break-all font-mono text-xs text-muted-foreground">
          {probe.checkedVmUuid ?? probe.recordedVmUuid}
        </p>
        {!probe.targetStatus && (
          <p className="text-xs text-muted-foreground">
            That power state is not one this flow acts on, so the status is left
            alone.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Record against infrastructure, plus the devices behind the disk total.
 *
 * One panel rather than two: the provider's vCPU, memory and disk figures ARE the "Infrastructure"
 * column, so printing them again above the table said everything twice. What the table cannot show
 * — the IP and the per-device breakdown — is kept, as a footer.
 */
function ConfigurationPanel({
  fields,
  provider,
}: {
  fields: SpecMismatchField[];
  provider: VmRecoveryProbe["provider"];
}) {
  const devices = [
    ...(provider?.disks ?? []).map((disk) => ({
      key: `disk-${disk.device}`,
      Icon: HardDrive,
      device: disk.device,
      detail: `${disk.sizeGb} GB${disk.type ? ` · ${disk.type}` : ""}${
        disk.detached ? " · detached" : ""
      }`,
    })),
    ...(provider?.networks ?? []).map((network) => ({
      key: `net-${network.device}`,
      Icon: Network,
      device: network.device,
      detail: `${network.ip ?? "no address"}${
        network.mac ? ` · ${network.mac}` : ""
      }`,
    })),
  ];

  return (
    <div className="overflow-hidden rounded-lg border">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Value</TableHead>
              <TableHead>WiCloud record</TableHead>
              <TableHead>Infrastructure</TableHead>
              <TableHead className="text-right">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {fields.map((field) => {
              const Icon = FIELD_ICONS[field.key] ?? Server;

              // Struck through only for a REAL difference. "Not reported" means the provider stayed
              // silent and WiCloud keeps what it holds — striking it there says the opposite of what
              // happens, on the one row where the operator most needs to know nothing is overwritten.
              const overwritten = field.status === "Mismatch";

              return (
                <TableRow key={field.key}>
                  <TableCell className="font-medium">
                    <span className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      {field.label}
                    </span>
                  </TableCell>
                  <TableCell
                    className={cn(
                      overwritten && "text-muted-foreground line-through",
                    )}
                  >
                    {formatValue(field.wiCloudValue, field.unit)}
                  </TableCell>
                  <TableCell className={cn(overwritten && "font-medium")}>
                    {formatValue(field.providerValue, field.unit)}
                  </TableCell>
                  <TableCell className="text-right">
                    {fieldStatusBadge(field)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {devices.length > 0 && (
        <div className="flex flex-wrap gap-x-5 gap-y-1 border-t bg-panel-2 px-4 py-2 text-xs text-muted-foreground">
          {devices.map(({ key, Icon, device, detail }) => (
            <span key={key} className="flex items-center gap-1.5">
              <Icon className="h-3 w-3 shrink-0" />
              <span className="font-mono">{device}</span>
              <span>{detail}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The commercial context an operator reads before committing. Labels above values rather than a
 * two-column key/value box: the order number is long enough to wrap against a right-aligned value,
 * and stacking makes three facts read in one glance instead of six.
 */
function ContextRow({ probe }: { probe: VmRecoveryProbe }) {
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-lg border p-3 text-sm sm:grid-cols-4">
      <Fact label="Current status">{probe.resourceStatus}</Fact>

      {/* Two columns wide, and the number is never truncated: its status is what step 5 acts on, so
          an ellipsis that eats "· Complete" hides the one fact this panel exists to show. */}
      <Fact label="Order" className="sm:col-span-2">
        {probe.orderNumber ? (
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-mono text-xs">{probe.orderNumber}</span>
            <Badge variant="outline">{probe.orderStatus}</Badge>
          </span>
        ) : (
          "none"
        )}
      </Fact>

      <Fact label="Expires">
        {probe.expiresAt ? formatDateTime(probe.expiresAt) : "—"}
      </Fact>
    </dl>
  );
}

function Fact({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn("min-w-0 space-y-1", className)}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-medium">{children}</dd>
    </div>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn("text-right", mono && "font-mono text-xs")}>
        {value}
      </span>
    </div>
  );
}

/** What the run actually did — including everything it deliberately left alone. */
function DoneStep({ execution }: { execution: VmRecoveryExecution }) {
  const complete = isComplete(execution.outcome);

  return (
    <div className="space-y-4">
      <Alert
        className={cn(
          complete
            ? "border-ok/25 bg-ok-soft [&>svg]:text-ok"
            : "border-warn/25 bg-warn-soft [&>svg]:text-warn",
        )}
      >
        {complete ? (
          <CheckCircle2 className="h-4 w-4" />
        ) : (
          <AlertTriangle className="h-4 w-4" />
        )}
        <AlertTitle>{complete ? "Recovered" : "Partly done"}</AlertTitle>
        <AlertDescription>{execution.summary}</AlertDescription>
      </Alert>

      {execution.warning && (
        <Alert className="border-warn/25 bg-warn-soft [&>svg]:text-warn">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Still to do</AlertTitle>
          <AlertDescription>{execution.warning}</AlertDescription>
        </Alert>
      )}

      <div className="grid gap-x-6 gap-y-1 rounded-lg border p-3 text-sm sm:grid-cols-2">
        <Row
          label="Status"
          value={`${execution.previousStatus} → ${execution.resourceStatus}`}
        />
        <Row label="VM UUID" value={execution.vmUuid ?? "—"} mono />
        <Row
          label="Order"
          value={
            execution.orderStatus
              ? `${execution.orderStatus}${execution.orderSettled ? " (closed now)" : ""}`
              : "none"
          }
        />
      </div>

      {execution.applied.length > 0 && (
        <div className="rounded-lg border p-3">
          <div className="mb-2 text-sm font-medium">
            Corrected from the machine
          </div>
          <div className="space-y-1 text-sm">
            {execution.applied.map((change) => (
              <div key={change.key} className="flex justify-between gap-3">
                <span className="text-muted-foreground">{change.label}</span>
                <span>
                  {formatValue(change.from, change.unit)} →{" "}
                  <span className="font-medium">
                    {formatValue(change.to, change.unit)}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        The billing period, the expiry date and every payment were left exactly
        as they were, and the customer was not notified.
      </p>
    </div>
  );
}
